import React, { useMemo, useState } from 'react';

const ActiveOrders = React.memo(({ userId = 'test_user', activeOrders = { orders: [] }, onCancelOrder, onModifyOrder }) => {
  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, order: null });
  
  // Memoize the orders with side information
  const allOrders = useMemo(() => {
    return activeOrders.orders.map(order => {
      // Determine side from order data - orders from database should have 'side' field (buy/sell)
      let side = 'BUY'; // default
      if (order.side) {
        side = order.side.toUpperCase();
      } else if (order.order_type) {
        // Fallback: try to infer from order_type if side is missing
        side = order.order_type.toUpperCase();
      }
      return { ...order, side };
    });
  }, [activeOrders.orders]);

  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '—';
  };

  const formatQuantity = (qty) => {
    return typeof qty === 'number' ? qty.toFixed(2) : '—';
  };

  const getOrderTypeIndicator = (orderType, triggerStatus) => {
    if (orderType === 'MARKET') {
      return <span className="market-order-indicator">MARKET</span>;
    }
    if (orderType === 'STOP_LIMIT') {
      const status = triggerStatus === 'pending' ? '⏳ PENDING' : triggerStatus === 'triggered' ? '⚡ TRIGGERED' : 'STOP_LIMIT';
      return <span className="stop-limit-order-indicator" title={`Stop Limit Order - Status: ${triggerStatus || 'pending'}`}>{status}</span>;
    }
    return <span className="limit-order-indicator">LIMIT</span>;
  };

  // Context menu handlers
  const handleContextMenu = (e, order) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      order: order
    });
  };

  const handleModifyClick = () => {
    if (contextMenu.order && onModifyOrder) {
      onModifyOrder(contextMenu.order);
    }
    setContextMenu({ show: false, x: 0, y: 0, order: null });
  };

  const handleCancelClick = () => {
    if (contextMenu.order && onCancelOrder) {
      onCancelOrder(contextMenu.order.order_id, contextMenu.order.user_id, contextMenu.order.symbol);
    }
    setContextMenu({ show: false, x: 0, y: 0, order: null });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, order: null });
  };

  return (
    <div>
      <table className="active-orders-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Side</th>
            <th>Price</th>
            <th>Stop/Limit</th>
            <th style={{width: '70px', textAlign: 'right'}}>Quantity</th>
            <th style={{width: '70px', textAlign: 'right'}}>Remaining</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {allOrders.length > 0 ? (
            allOrders.map((order) => (
                     <tr 
                       key={order.order_id}
                      onContextMenu={(e) => handleContextMenu(e, order)}
                      onDoubleClick={() => {
                        if (onModifyOrder) {
                          onModifyOrder(order);
                        }
                      }}
                       style={{ cursor: 'context-menu' }}
                       title="Right-click for menu, double-click to modify"
                     >
                <td>
                  <div style={{display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap'}}>
                    {order.symbol && (
                      <span style={{
                        fontSize: '8px',
                        padding: '1px 3px',
                        background: 'var(--panel)',
                        border: '1px solid var(--border)',
                        borderRadius: '3px',
                        color: 'var(--muted)',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        lineHeight: '1.2'
                      }}>
                        {order.symbol}
                      </span>
                    )}
                    {getOrderTypeIndicator(order.order_type || 'LIMIT', order.trigger_status)}
                  </div>
                </td>
                <td style={{color: order.side === 'BUY' ? 'var(--good)' : 'var(--bad)'}}>
                  {order.side}
                </td>
                <td>
                  {order.order_type === 'MARKET' ? 'Market' : 
                   order.order_type === 'STOP_LIMIT' && order.trigger_status === 'pending' 
                     ? `Stop: ${formatPrice(order.stop_price)}` 
                     : formatPrice(order.price)}
                </td>
                <td>
                  {order.order_type === 'STOP_LIMIT' ? (
                    <span title={`Stop: ${formatPrice(order.stop_price)}, Limit: ${formatPrice(order.limit_price)}`}>
                      {order.trigger_status === 'pending' 
                        ? `Limit: ${formatPrice(order.limit_price)}` 
                        : formatPrice(order.limit_price || order.price)}
                    </span>
                  ) : '—'}
                </td>
                <td style={{width: '70px', textAlign: 'right', fontSize: '12px'}}>{formatQuantity(order.quantity)}</td>
                <td style={{width: '70px', textAlign: 'right', fontSize: '12px'}}>{formatQuantity(order.remaining_quantity || order.quantity)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button 
                      className="modify-btn"
                      onClick={(e) => {
                        if (onModifyOrder) {
                          onModifyOrder(order, e);
                        }
                      }}
                      style={{background: 'transparent', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: '6px', color: 'var(--muted)', cursor: 'pointer', fontSize: '13px'}}
                    >
                      Modify
                    </button>
                    <button 
                      className="cancel-btn"
                      onClick={() => {
                        if (onCancelOrder) {
                          onCancelOrder(order.order_id, order.user_id, order.symbol);
                        }
                      }}
                      style={{background: 'transparent', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: '6px', color: 'var(--muted)', cursor: 'pointer', fontSize: '13px'}}
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{textAlign: 'center', color: 'var(--muted)', padding: '20px'}}>
                No active orders
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
             {/* Context Menu */}
             {contextMenu.show && (
               <div
                 style={{
                   position: 'fixed',
                   top: contextMenu.y,
                   left: contextMenu.x,
                   background: 'var(--panel)',
                   border: '1px solid var(--border)',
                   borderRadius: '6px',
                   boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                   zIndex: 1000,
                   minWidth: '120px'
                 }}
                 onClick={closeContextMenu}
               >
                 <div style={{padding: '4px 8px', fontSize: '10px', color: '#666', borderBottom: '1px solid var(--border)'}}>
                   Order: {contextMenu.order?.order_id?.substring(0, 8)}...
                 </div>
          <div
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid var(--border)',
              fontSize: '13px',
              color: 'var(--text)'
            }}
            onClick={handleModifyClick}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Modify Order
          </div>
          <div
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '13px',
              color: 'var(--text)'
            }}
            onClick={handleCancelClick}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Cancel Order
          </div>
        </div>
      )}
    </div>
  );
});

ActiveOrders.displayName = 'ActiveOrders';

export default ActiveOrders;